/*! step-bundle — guided multi-step bundle builder
 *  Pairs with sections/step-bundle.liquid + snippets/step-bundle-step.liquid.
 *  The page only stages the right variants in the cart; the actual free/discounted
 *  pricing comes from a Shopify automatic discount, shown in the cart & at checkout.
 */
if (!customElements.get('step-bundle')) {
  customElements.define(
    'step-bundle',
    class StepBundle extends HTMLElement {
      constructor() {
        super();

        // selections: Map keyed by `${stepId}:${variantId}` → selection record
        this.selections = new Map();
        this.autoAdvanced = new Set();

        this.wrapper = this.closest('.step-bundle-wrapper') || this.parentElement;

        // Variant controls live in the steps column (sibling of this element),
        // so we delegate their clicks from the shared wrapper.
        this.wrapper.addEventListener('click', this.onVariantClick.bind(this));
        // Remove buttons + submit live inside this element.
        this.addEventListener('click', this.onSidebarClick.bind(this));

        this.renderSidebar();
        this.refreshSteps();
      }

      get steps() {
        return Array.from(this.wrapper.querySelectorAll('[data-step]'));
      }

      get body() {
        return this.querySelector('[data-step-bundle-body]');
      }

      get submitButton() {
        return this.querySelector('[data-step-bundle-submit]');
      }

      get cartDrawer() {
        return document.querySelector('cart-drawer');
      }

      get preventDuplicate() {
        return this.hasAttribute('data-prevent-duplicate');
      }

      get requiredSteps() {
        return parseInt(this.getAttribute('data-required-steps')) || 0;
      }

      /* ---------- selection ---------- */

      onVariantClick(event) {
        const control = event.target.closest('[data-step-variant]');
        if (!control || !this.wrapper.contains(control)) return;
        if (control.disabled || control.getAttribute('aria-disabled') === 'true' || control.dataset.available === 'false') return;

        const step = control.closest('[data-step]');
        if (!step) return;

        const key = this.keyFor(step, control);
        const max = parseInt(step.dataset.max) || 1;

        if (this.selections.has(key)) {
          this.deselect(key);
          this.afterChange(step);
          return;
        }

        const variantId = control.dataset.variantId;
        if (this.preventDuplicate && this.variantSelectedElsewhere(variantId, key)) {
          this.flash(step);
          return;
        }

        const inStep = this.selectionsInStep(step);
        if (inStep.length >= max) {
          if (max === 1) {
            this.deselect(inStep[0].key);
          } else {
            this.flash(step);
            return;
          }
        }

        this.select(step, control);
        this.afterChange(step);
      }

      keyFor(step, control) {
        return `${step.dataset.stepId}:${control.dataset.variantId}`;
      }

      selectionsInStep(step) {
        return Array.from(this.selections.values()).filter((s) => s.stepId === step.dataset.stepId);
      }

      variantSelectedElsewhere(variantId, exceptKey) {
        for (const [key, s] of this.selections) {
          if (key !== exceptKey && s.variantId === variantId) return true;
        }
        return false;
      }

      select(step, control) {
        const key = this.keyFor(step, control);
        const d = control.dataset;
        this.selections.set(key, {
          key,
          stepId: step.dataset.stepId,
          stepIndex: parseInt(step.dataset.stepIndex) || 0,
          control,
          variantId: d.variantId,
          productId: d.productId,
          price: parseInt(d.price) || 0,
          compare: parseInt(d.compare) || 0,
          title: d.title || '',
          variantTitle: d.variantTitle || '',
          image: d.image || '',
          reward: control.hasAttribute('data-reward'),
          rewardLabel: d.rewardLabel || 'FREE',
        });

        control.setAttribute('aria-pressed', 'true');
        control.classList.add('is-selected');

        // Card mode: reflect the chosen price on the parent card.
        const card = control.closest('[data-step-product]');
        if (card) {
          const priceEl = card.querySelector('[data-card-price]');
          if (priceEl && !priceEl.dataset.original) priceEl.dataset.original = priceEl.innerHTML;
          if (priceEl) priceEl.innerHTML = this.formatMoney(parseInt(d.price) || 0);
          card.classList.add('is-selected');
        }
      }

      deselect(key) {
        const s = this.selections.get(key);
        if (!s) return;
        this.selections.delete(key);

        s.control.setAttribute('aria-pressed', 'false');
        s.control.classList.remove('is-selected');

        const card = s.control.closest('[data-step-product]');
        if (card && !card.querySelector('[data-step-variant].is-selected')) {
          const priceEl = card.querySelector('[data-card-price]');
          if (priceEl && priceEl.dataset.original) priceEl.innerHTML = priceEl.dataset.original;
          card.classList.remove('is-selected');
        }
      }

      onSidebarClick(event) {
        const remove = event.target.closest('[data-line-remove]');
        if (remove) {
          const key = remove.dataset.lineRemove;
          const step = this.selections.get(key)?.control.closest('[data-step]');
          this.deselect(key);
          if (step) this.afterChange(step);
          else this.afterChange(this.steps[0]);
          return;
        }

        if (event.target.closest('[data-step-bundle-submit]')) {
          this.onSubmit(event);
        }
      }

      /* ---------- post-change updates ---------- */

      afterChange(step) {
        this.renderSidebar();
        this.refreshSteps();
        this.updateTotals();
        this.updateProgress();
        this.updateSubmit();
        this.maybeAdvance(step);
      }

      refreshSteps() {
        this.steps.forEach((step) => {
          const min = parseInt(step.dataset.min) || 0;
          const count = this.selectionsInStep(step).length;
          const satisfied = count >= min && min > 0;
          step.classList.toggle('is-complete', satisfied);

          const status = step.querySelector('[data-step-status]');
          if (status) {
            if (satisfied) {
              status.textContent = theme?.strings?.added || 'Added';
              status.classList.add('is-complete');
            } else if (min > 0) {
              status.textContent = `Select ${min - count > 0 ? min - count : min}`;
              status.classList.remove('is-complete');
            } else {
              status.textContent = 'Optional';
              status.classList.remove('is-complete');
            }
          }
        });
      }

      renderSidebar() {
        const body = this.body;
        if (!body) return;

        const lines = Array.from(this.selections.values()).sort(
          (a, b) => a.stepIndex - b.stepIndex
        );

        const empty = body.querySelector('[data-step-bundle-empty]');
        if (empty) empty.hidden = lines.length > 0;

        // Clear previously rendered lines (keep the empty-state node).
        body.querySelectorAll('[data-line]').forEach((el) => el.remove());

        lines.forEach((s) => {
          const line = document.createElement('div');
          line.className = 'horizontal-product step-bundle-line flex items-center gap-3';
          line.setAttribute('data-line', '');
          line.dataset.lineKey = s.key;

          const priceHtml = s.reward
            ? `<s class="text-opacity">${this.formatMoney(s.price)}</s> <strong class="step-bundle-line__free">${this.escape(s.rewardLabel)}</strong>`
            : this.formatMoney(s.price);

          line.innerHTML = `
            <figure class="step-bundle-line__media media media--square media--contain aspect-square shrink-0 overflow-hidden">
              ${s.image ? `<img src="${s.image}" alt="${this.escape(s.title)}" width="120" height="120" loading="lazy" />` : ''}
            </figure>
            <div class="grow flex flex-col gap-0d5 leading-tight">
              <p class="font-medium text-sm leading-tight">${this.escape(s.title)}</p>
              ${s.variantTitle && s.variantTitle !== s.title ? `<p class="text-xs text-opacity">${this.escape(s.variantTitle)}</p>` : ''}
              <p class="text-sm">${priceHtml}</p>
            </div>
            <button type="button" class="link cursor-pointer text-xs shrink-0" data-line-remove="${s.key}">${theme?.strings?.remove || 'Remove'}</button>
          `;
          body.appendChild(line);
        });
      }

      updateTotals() {
        let total = 0;
        let savings = 0;
        this.selections.forEach((s) => {
          if (s.reward) savings += s.price;
          else total += s.price;
        });

        const totalEl = this.querySelector('[data-step-bundle-total]');
        if (totalEl) totalEl.innerHTML = this.formatMoney(total);

        const savingsRow = this.querySelector('[data-step-bundle-savings-row]');
        const savingsEl = this.querySelector('[data-step-bundle-savings]');
        if (savingsEl) savingsEl.innerHTML = `&minus;${this.formatMoney(savings)}`;
        if (savingsRow) savingsRow.hidden = savings <= 0;
      }

      updateProgress() {
        const done = this.steps.filter((step) => {
          const min = parseInt(step.dataset.min) || 0;
          return min > 0 && this.selectionsInStep(step).length >= min;
        }).length;

        const countEl = this.querySelector('[data-step-bundle-progress-count]');
        if (countEl) countEl.textContent = done;

        const bar = this.querySelector('[data-step-bundle-progress]');
        if (bar) {
          const pct = this.requiredSteps > 0 ? Math.min(100, (100 * done) / this.requiredSteps) : 0;
          bar.style.setProperty('--progress', `${pct}%`);
        }
      }

      allSatisfied() {
        return this.steps.every((step) => {
          const min = parseInt(step.dataset.min) || 0;
          if (min === 0) return true;
          return this.selectionsInStep(step).length >= min;
        });
      }

      updateSubmit() {
        if (this.submitButton) this.submitButton.disabled = !this.allSatisfied() || this.selections.size === 0;
      }

      maybeAdvance(step) {
        if (!step || !step.hasAttribute('data-auto-advance')) return;
        const min = parseInt(step.dataset.min) || 0;
        if (min === 0 || this.selectionsInStep(step).length < min) return;
        if (this.autoAdvanced.has(step.dataset.stepId)) return;
        this.autoAdvanced.add(step.dataset.stepId);

        const next = this.steps.find((s) => parseInt(s.dataset.stepIndex) > parseInt(step.dataset.stepIndex));
        const target = next || this; // last step → nudge toward the summary/CTA
        this.scrollTo(target);
      }

      scrollTo(el) {
        if (theme?.config?.motionReduced) return;
        const header = document.querySelector('.section-header.sticky, sticky-header, .header-sticky');
        const offset = (header ? header.clientHeight : 0) + 24;
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: top < 0 ? 0 : top, behavior: 'smooth' });
      }

      flash(step) {
        step.classList.remove('step-bundle-step--flash');
        void step.offsetWidth; // restart animation
        step.classList.add('step-bundle-step--flash');
      }

      /* ---------- add to cart ---------- */

      onSubmit(event) {
        if (this.submitButton.disabled || this.submitButton.hasAttribute('aria-disabled')) return;

        const data = {
          items: Array.from(this.selections.values())
            .sort((a, b) => a.stepIndex - b.stepIndex)
            .map((s) => ({ id: s.variantId, quantity: 1 })),
        };

        if (document.body.classList.contains('template-cart') || theme.settings.cartType === 'page') {
          theme.utils.postLink2(theme.routes.cart_add_url, { parameters: { ...data } });
          return;
        }

        event.preventDefault();
        this.activeElement = event.submitter || this.submitButton;
        this.handleErrorMessage();

        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        const body = JSON.stringify({ ...data, sections: sectionsToBundle, sections_url: window.location.pathname });

        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.setAttribute('aria-busy', 'true');

        fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig('javascript'), body })
          .then((response) => response.json())
          .then(async (parsedState) => {
            if (parsedState.status) {
              this.handleErrorMessage(parsedState.description);
              document.dispatchEvent(
                new CustomEvent('ajaxProduct:error', { detail: { errorMessage: parsedState.description } })
              );
              return;
            }

            const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
            cartJson['sections'] = parsedState['sections'];

            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'step-bundle', cart: cartJson });
            document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsedState } }));

            this.cartDrawer?.show(this.activeElement);
            this.reset();
          })
          .catch((error) => console.log(error))
          .finally(() => {
            this.submitButton.removeAttribute('aria-busy');
            this.submitButton.removeAttribute('aria-disabled');
          });
      }

      handleErrorMessage(message = false) {
        this.errorMessage = this.errorMessage || this.querySelector('.product-form__error-message');
        if (!this.errorMessage) return;
        this.errorMessage.toggleAttribute('hidden', !message);
        this.errorMessage.textContent = message || '';
      }

      reset() {
        Array.from(this.selections.keys()).forEach((key) => this.deselect(key));
        this.autoAdvanced.clear();
        this.renderSidebar();
        this.refreshSteps();
        this.updateTotals();
        this.updateProgress();
        this.updateSubmit();
      }

      /* ---------- helpers ---------- */

      formatMoney(cents) {
        if (window.theme?.Currency?.formatMoney) {
          return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
        }
        return `$${(cents / 100).toFixed(2)}`;
      }

      escape(str) {
        const div = document.createElement('div');
        div.textContent = str == null ? '' : str;
        return div.innerHTML;
      }
    }
  );
}

if (!customElements.get('step-bundle-toggle-button')) {
  customElements.define(
    'step-bundle-toggle-button',
    class StepBundleToggleButton extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('click', () => {
          const target = this.hasAttribute('aria-controls')
            ? document.getElementById(this.getAttribute('aria-controls'))
            : this.closest('step-bundle');
          target?.classList.toggle('is-open');
        });
      }
    }
  );
}